import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AddEmployeeLayer = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        employee_name: '',
        position: '',
        department: '',
        expire_date: '',
        status: 'active',
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!validTypes.includes(file.type)) {
                setValidationErrors(prev => ({
                    ...prev,
                    image: ['Please select a valid image file (JPEG, JPG, PNG, or GIF)']
                }));
                return;
            }

            // Validate file size (max 2MB)
            if (file.size > 2048 * 1024) {
                setValidationErrors(prev => ({
                    ...prev,
                    image: ['Image size must be less than 2MB']
                }));
                return;
            }

            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
            
            // Clear validation error for image
            if (validationErrors.image) {
                setValidationErrors(prev => ({
                    ...prev,
                    image: ''
                }));
            }
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
        // Clear file input
        const fileInput = document.getElementById('image');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        setValidationErrors({});
        setSuccessMessage('');

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            // Create FormData for file upload
            const formDataToSend = new FormData();
            formDataToSend.append('employee_name', formData.employee_name);
            formDataToSend.append('position', formData.position);
            formDataToSend.append('department', formData.department);
            formDataToSend.append('expire_date', formData.expire_date);
            formDataToSend.append('status', formData.status);
            
            if (imageFile) {
                formDataToSend.append('image', imageFile);
            }

            const response = await fetch(`${apiUrl}/employees`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    // Don't set Content-Type header - let browser set it with boundary for FormData
                },
                body: formDataToSend,
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.clear();
                    navigate('/sign-in', { replace: true });
                    return;
                }
                if (response.status === 422 && data.errors) {
                    setValidationErrors(data.errors);
                    throw new Error('Please fix the validation errors');
                }
                throw new Error(data.message || 'Failed to create employee');
            }

            if (data.success) {
                setSuccessMessage(`Employee created successfully! Employee Code: ${data.data.code}`);
                // Reset form
                setFormData({
                    employee_name: '',
                    position: '',
                    department: '',
                    expire_date: '',
                    status: 'active',
                });
                removeImage();
                // Scroll to top to show success message
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (err) {
            console.error('Error creating employee:', err);
            setError(err.message || 'An error occurred while creating employee');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReset = () => {
        setFormData({
            employee_name: '',
            position: '',
            department: '',
            expire_date: '',
            status: 'active',
        });
        removeImage();
        setError('');
        setValidationErrors({});
        setSuccessMessage('');
    };

    return (
        <div className='card h-100 p-0 radius-12'>
            <div className='card-header border-bottom bg-base py-16 px-24'>
                <h6 className='text-lg fw-semibold mb-0'>Register New Employee</h6>
            </div>
            <div className='card-body p-24'>
                {error && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        <strong>Error!</strong> {error}
                        <button type="button" className="btn-close" onClick={() => setError('')}></button>
                    </div>
                )}
                {successMessage && (
                    <div className="alert alert-success alert-dismissible fade show" role="alert">
                        <strong>Success!</strong> {successMessage}
                        <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
                    </div>
                )}
                
                <div className="alert alert-info" role="alert">
                    <i className="ri-information-line me-2"></i>
                    <strong>Note:</strong> Employee code will be generated automatically (12 characters with uppercase letters and numbers)
                </div>

                <form onSubmit={handleSubmit}>
                    <div className='row'>
                        <div className='col-sm-6'>
                            <div className='mb-20'>
                                <label htmlFor='employee_name' className='form-label fw-semibold text-primary-light text-sm mb-8'>
                                    Magaca / Name <span className='text-danger-600'>*</span>
                                </label>
                                <input
                                    type='text'
                                    className={`form-control radius-8 ${validationErrors.employee_name ? 'is-invalid' : ''}`}
                                    id='employee_name'
                                    name='employee_name'
                                    placeholder='Enter employee full name'
                                    value={formData.employee_name}
                                    onChange={handleInputChange}
                                    required
                                />
                                {validationErrors.employee_name && (
                                    <div className="invalid-feedback">
                                        {validationErrors.employee_name[0]}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='col-sm-6'>
                            <div className='mb-20'>
                                <label htmlFor='position' className='form-label fw-semibold text-primary-light text-sm mb-8'>
                                   Xilka / Position <span className='text-danger-600'>*</span>
                                </label>
                                <input
                                    type='text'
                                    className={`form-control radius-8 ${validationErrors.position ? 'is-invalid' : ''}`}
                                    id='position'
                                    name='position'
                                    placeholder='Enter position title'
                                    value={formData.position}
                                    onChange={handleInputChange}
                                    required
                                />
                                {validationErrors.position && (
                                    <div className="invalid-feedback">
                                        {validationErrors.position[0]}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='col-sm-6'>
                            <div className='mb-20'>
                                <label htmlFor='department' className='form-label fw-semibold text-primary-light text-sm mb-8'>
                                   Waax / Department <span className='text-danger-600'>*</span>
                                </label>
                                <input
                                    type='text'
                                    className={`form-control radius-8 ${validationErrors.department ? 'is-invalid' : ''}`}
                                    id='department'
                                    name='department'
                                    placeholder='Enter department name'
                                    value={formData.department}
                                    onChange={handleInputChange}
                                    required
                                />
                                {validationErrors.department && (
                                    <div className="invalid-feedback">
                                        {validationErrors.department[0]}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='col-sm-6'>
                            <div className='mb-20'>
                                <label htmlFor='expire_date' className='form-label fw-semibold text-primary-light text-sm mb-8'>
                                    Dhacayo / Expiry Date <span className='text-danger-600'>*</span>
                                </label>
                                <input
                                    type='date'
                                    className={`form-control radius-8 ${validationErrors.expire_date ? 'is-invalid' : ''}`}
                                    id='expire_date'
                                    name='expire_date'
                                    value={formData.expire_date}
                                    onChange={handleInputChange}
                                    min={new Date().toISOString().split('T')[0]}
                                    required
                                />
                                {validationErrors.expire_date && (
                                    <div className="invalid-feedback">
                                        {validationErrors.expire_date[0]}
                                    </div>
                                )}
                                <small className="text-muted">Required field</small>
                            </div>
                        </div>
                        <div className='col-sm-6'>
                            <div className='mb-20'>
                                <label htmlFor='status' className='form-label fw-semibold text-primary-light text-sm mb-8'>
                                    Status <span className='text-danger-600'>*</span>
                                </label>
                                <select
                                    className={`form-select radius-8 ${validationErrors.status ? 'is-invalid' : ''}`}
                                    id='status'
                                    name='status'
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value='active'>Active</option>
                                    <option value='inactive'>Inactive</option>
                                </select>
                                {validationErrors.status && (
                                    <div className="invalid-feedback">
                                        {validationErrors.status[0]}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='col-sm-6'>
                            <div className='mb-20'>
                                <label htmlFor='image' className='form-label fw-semibold text-primary-light text-sm mb-8'>
                                    Employee Image <span className='text-danger-600'>*</span>
                                </label>
                                <input
                                    type='file'
                                    className={`form-control radius-8 ${validationErrors.image ? 'is-invalid' : ''}`}
                                    id='image'
                                    name='image'
                                    accept='image/jpeg,image/jpg,image/png,image/gif'
                                    onChange={handleImageChange}
                                    required
                                />
                                {validationErrors.image && (
                                    <div className="invalid-feedback">
                                        {validationErrors.image[0]}
                                    </div>
                                )}
                                <small className="text-muted">Accepted formats: JPEG, JPG, PNG, GIF (Max: 2MB)</small>
                            </div>
                        </div>

                        {/* Image Preview */}
                        {imagePreview && (
                            <div className='col-12'>
                                <div className='mb-20'>
                                    <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                                        Image Preview
                                    </label>
                                    <div className='position-relative d-inline-block'>
                                        <img 
                                            src={imagePreview} 
                                            alt='Employee Preview' 
                                            style={{ 
                                                maxWidth: '200px', 
                                                maxHeight: '200px', 
                                                objectFit: 'cover',
                                                borderRadius: '8px',
                                                border: '2px solid #e0e0e0'
                                            }} 
                                        />
                                        <button
                                            type='button'
                                            className='btn btn-danger btn-sm position-absolute'
                                            style={{ top: '5px', right: '5px' }}
                                            onClick={removeImage}
                                        >
                                            <i className='ri-close-line'></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className='d-flex align-items-center justify-content-center gap-3 mt-24'>
                        <button
                            type='button'
                            className='border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8'
                            onClick={handleReset}
                            disabled={isSubmitting}
                        >
                            Reset
                        </button>
                        <button
                            type='submit'
                            className='btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8'
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Registering...
                                </>
                            ) : (
                                'Register Employee'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddEmployeeLayer;
